/**
 * Service for chat session sharing operations.
 *
 * Handles all API calls related to sharing chat sessions with users and groups.
 */

import api, { apiEndpoints } from '../lib/api'
import type {
  ChatSessionShare,
  EligibleShareTargets,
  ShareAccessLevel,
  SessionParticipant,
  UserMinimal,
  GroupMinimal,
} from '../types/agents'

/**
 * Maps API response to ChatSessionShare interface.
 */
function mapShareFromApi(data: Record<string, unknown>): ChatSessionShare {
  return {
    id: String(data.id),
    sessionId: String(
      data.session_id || (data.session as Record<string, unknown>)?.id || ''
    ),
    sharedWithUser: data.shared_with_user
      ? mapUserFromApi(data.shared_with_user as Record<string, unknown>)
      : null,
    sharedWithGroup: data.shared_with_group
      ? mapGroupFromApi(data.shared_with_group as Record<string, unknown>)
      : null,
    accessLevel: String(data.access_level) as ShareAccessLevel,
    sharedBy: mapUserFromApi(data.shared_by as Record<string, unknown>),
    targetName: String(data.target_name || ''),
    targetType: String(data.target_type || 'user') as 'user' | 'group',
    createdAt: String(data.created_at || new Date().toISOString()),
  }
}

/**
 * Maps API response to UserMinimal interface.
 */
function mapUserFromApi(data: Record<string, unknown>): UserMinimal {
  return {
    id: Number(data.id),
    username: String(data.username || ''),
    email: String(data.email || ''),
    fullName: String(data.full_name || data.fullName || ''),
  }
}

/**
 * Maps API response to GroupMinimal interface.
 */
function mapGroupFromApi(data: Record<string, unknown>): GroupMinimal {
  return {
    id: Number(data.id),
    name: String(data.name || ''),
  }
}

/**
 * Maps API response to SessionParticipant interface.
 */
function mapParticipantFromApi(
  data: Record<string, unknown>
): SessionParticipant {
  return {
    user: mapUserFromApi(data.user as Record<string, unknown>),
    accessLevel: String(data.access_level) as ShareAccessLevel | 'owner',
    isOwner: Boolean(data.is_owner),
    joinedAt: String(data.joined_at || new Date().toISOString()),
  }
}

export const sharingApi = {
  /**
   * Get all shares for a session.
   */
  async getShares(sessionId: string): Promise<ChatSessionShare[]> {
    const res = await api.get(apiEndpoints.chat.shares(sessionId))
    const shares = (res.data?.shares || res.data || []) as Record<
      string,
      unknown
    >[]
    return shares.map(mapShareFromApi)
  },

  /**
   * Create new shares for a session.
   */
  async createShares(
    sessionId: string,
    params: {
      users?: number[]
      groups?: number[]
      accessLevel: ShareAccessLevel
    }
  ): Promise<ChatSessionShare[]> {
    const res = await api.post(apiEndpoints.chat.shares(sessionId), {
      users: params.users || [],
      groups: params.groups || [],
      access_level: params.accessLevel,
    })
    const created = (res.data?.created || res.data || []) as Record<
      string,
      unknown
    >[]
    return created.map(mapShareFromApi)
  },

  /**
   * Update a share's access level.
   */
  async updateShare(
    sessionId: string,
    shareId: string,
    accessLevel: ShareAccessLevel
  ): Promise<ChatSessionShare> {
    const res = await api.patch(
      apiEndpoints.chat.shareDetail(sessionId, shareId),
      {
        access_level: accessLevel,
      }
    )
    return mapShareFromApi(res.data as Record<string, unknown>)
  },

  /**
   * Delete a share (revoke access).
   */
  async deleteShare(sessionId: string, shareId: string): Promise<void> {
    await api.delete(apiEndpoints.chat.shareDetail(sessionId, shareId))
  },

  /**
   * Get eligible targets (users and groups) for sharing.
   */
  async getEligibleTargets(sessionId: string): Promise<EligibleShareTargets> {
    const res = await api.get(apiEndpoints.chat.eligibleTargets(sessionId))
    const data = res.data as Record<string, unknown>
    return {
      users: ((data.users || []) as Record<string, unknown>[]).map(
        mapUserFromApi
      ),
      groups: ((data.groups || []) as Record<string, unknown>[]).map(
        mapGroupFromApi
      ),
    }
  },

  /**
   * Get all participants of a session (owner + shared users).
   */
  async getParticipants(sessionId: string): Promise<SessionParticipant[]> {
    const res = await api.get(apiEndpoints.chat.participants(sessionId))
    const participants = (res.data?.participants || res.data || []) as Record<
      string,
      unknown
    >[]
    return participants.map(mapParticipantFromApi)
  },

  /**
   * Transfer ownership of a session to another user.
   */
  async transferOwnership(
    sessionId: string,
    newOwnerId: number
  ): Promise<{ message: string }> {
    const res = await api.post(apiEndpoints.chat.transferOwnership(sessionId), {
      new_owner_id: newOwnerId,
    })
    return { message: String(res.data?.message || 'Ownership transferred') }
  },

  /**
   * Get sessions shared with the current user.
   */
  async getSharedWithMe(): Promise<Record<string, unknown>[]> {
    const res = await api.get(apiEndpoints.chat.sharedWithMe)
    return (res.data?.results || res.data || []) as Record<string, unknown>[]
  },

  /**
   * Mark a session as read by the current user.
   */
  async markSessionRead(sessionId: string): Promise<{ success: boolean }> {
    const res = await api.post(apiEndpoints.chat.markRead(sessionId))
    return { success: Boolean(res.data?.success) }
  },

  /**
   * Get count of unread shared sessions.
   */
  async getUnreadCount(): Promise<UnreadCountResponse> {
    const res = await api.get(apiEndpoints.chat.unreadCount)
    const data = res.data as Record<string, unknown>
    return {
      unreadCount: Number(data.unread_count || 0),
      sessions: ((data.sessions || []) as Record<string, unknown>[]).map(s => ({
        sessionId: String(s.session_id),
        title: String(s.title || 'Sem título'),
        lastActivity: String(s.last_activity),
        lastReadAt: s.last_read_at ? String(s.last_read_at) : null,
      })),
    }
  },
}

/**
 * Response type for unread count endpoint.
 */
export interface UnreadCountResponse {
  unreadCount: number
  sessions: Array<{
    sessionId: string
    title: string
    lastActivity: string
    lastReadAt: string | null
  }>
}

export default sharingApi
